import { useMemo, useState } from 'react'
import { Badge, Box, Dialog, Tooltip } from '@mui/material'
import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import { SyncErrorIcon } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import useResetFlowValve from '@/features/config/hooks/hydraulic/flowValve/useResetFlowValve'
import useSearchHydraulic from '@/features/config/hooks/hydraulic/useSearchHydraulic'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { HydraulicType } from '@/types/hydraulic'

const countPipeErrors = (pipes: HydraulicType[] | undefined): number => {
  if (!pipes || pipes.length === 0) return 0
  let count = 0
  const traverse = (items: HydraulicType[]) => {
    for (const item of items) {
      if (item.status && item.status !== 'Ok') count += 1
      if (item.subPipes?.length) traverse(item.subPipes)
    }
  }
  traverse(pipes)
  return count
}

const HydraulicAckButton = () => {
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const { mutate: resetFlowValve, isLoading: isAcknowledging } =
    useResetFlowValve()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: pipeData } = useSearchHydraulic(
    { PageSize: 0, PageIndex: 1 },
    Number(interfaceId || 0),
    { enabled: Boolean(interfaceId) } as any,
  )

  const errorCount = useMemo(
    () => countPipeErrors(pipeData?.list as HydraulicType[] | undefined),
    [pipeData],
  )

  const handleOpen = () => {
    if (isViewOnly || isAcknowledging) return
    setConfirmOpen(true)
  }

  const handleClose = () => {
    if (isAcknowledging) return
    setConfirmOpen(false)
  }

  const handleConfirm = () => {
    if (isViewOnly) return
    resetFlowValve(Number(interfaceId), {
      onSuccess: () => {
        setConfirmOpen(false)
      },
    })
  }

  if (errorCount === 0) return null

  return (
    <>
      <Tooltip title={t('errors.acknowledge')} arrow>
        <Box
          component="span"
          onClick={handleOpen}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            opacity: isViewOnly ? 0.5 : 1,
            cursor: isViewOnly ? 'not-allowed' : 'pointer',
            pointerEvents: isViewOnly ? 'none' : 'auto',
          }}
        >
          <Badge
            badgeContent={errorCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '9px',
                height: '14px',
                minWidth: '14px',
                padding: '0 4px',
              },
            }}
          >
            <SyncErrorIcon sx={{ color: '#EF4136', fontSize: '18px' }} />
          </Badge>
        </Box>
      </Tooltip>
      <Dialog open={confirmOpen} onClose={handleClose}>
        <ConfirmDeleteModalUI
          title={t('errors.acknowledge')}
          text={t('hydraulicAckConfirmText')}
          confirmText={t('errors.acknowledge')}
          cancelText={t('commonActionsCancel')}
          isLoading={isAcknowledging}
          onClose={handleClose}
          onConfirm={handleConfirm}
        />
      </Dialog>
    </>
  )
}

export default HydraulicAckButton
